<template>
  <div>
    <ServicesHeroPortal
      v-if="!isAuthenticated"
      :title="general.title"
      :icon="general.icon"
      :sub-title="general.subTitle"
      :list-items="general.listItems"
      :images="general.images"
      :services="services"
    />
    <div
      class="mx-auto flex w-full max-w-content-container flex-col justify-center gap-y-7 px-small pb-20 md:items-start xl:pb-36 xl:pl-16 xl:pr-0"
      :class="{
        'pt-8 xl:pt-10': isAuthenticated,
      }"
    >
      <Breadcrumb v-if="isAuthenticated" :menu="breadcrumbStore.items" />

      <div class="relative h-full w-full">
        <SectionTitle
          v-if="isAuthenticated"
          data-testid="portal-title"
          class="mb-6 ml-0 lg:ml-5"
          :title="general.title"
          :icon="general.icon"
          :mineral="general.category"
          authorized
        />

        <slot name="daily-indicator" />

        <div class="mt-5 pb-0 lg:mt-8">
          <div class="flex grid-cols-5 flex-col gap-x-3 gap-y-6 xl:grid">
            <div
              v-if="hasGlobalWeightedAverage"
              class="col-span-3 rounded-xl bg-s-300"
              :class="loadingPricesPortal ? 'animate-pulse' : ''"
            >
              <slot name="globalWeightedAverage" />
            </div>
            <div
              class="flex flex-col gap-2.5"
              :class="{
                'col-span-5 grid-cols-2 xl:grid': !hasGlobalWeightedAverage,
                'col-span-2': hasGlobalWeightedAverage,
              }"
            >
              <div
                class="flex min-h-[220px] w-full animate-pulse items-center justify-center rounded-xl bg-s-300 xl:min-h-[381px]"
                :class="{
                  '!animate-none': !isLoading,
                  'xl:!min-h-[274px]': !hasGlobalWeightedAverage,
                }"
              >
                <CarouselArticle
                  v-if="articles.length"
                  :market="general.slug"
                  :articles="articles"
                  class="w-full"
                  :class="{
                    'xl:h-[274px]': !hasGlobalWeightedAverage,
                    'xl:h-[381px]': hasGlobalWeightedAverage,
                  }"
                />
              </div>
              <div
                class="relative h-[181px] grow rounded-xl bg-s-300 md:h-60 xl:h-auto"
              >
                <CarouselCapexTracker
                  class="!relative rounded-xl [&_img]:rounded-xl"
                  button-classes="!flex bg-white !text-black border-2"
                  :material="ALL_MATERIALS_TOTALS.mineral"
                  :value="ALL_MATERIALS_TOTALS.value"
                  :image-url="ALL_MATERIALS_TOTALS.imageUrl"
                />
              </div>
            </div>
          </div>

          <div class="relative mt-5 w-full max-w-content-container">
            <div class="mb-[58px] mt-16 w-full">
              <div
                v-if="isLoading || !isClient"
                class="flex h-[444px] w-full items-center justify-center"
              >
                <TheLoader />
              </div>
              <ClientOnly v-else>
                <ReportsAndModelsSection
                  :reports="reports"
                  :product="product"
                  :category="category"
                />
              </ClientOnly>
            </div>
          </div>

          <div class="relative mt-5 w-full max-w-content-container">
            <VideoSection :product-slug="general.slug" :videos="videos" />
          </div>

          <div
            v-if="hasSustainabilityIndex"
            class="mt-8 w-full max-w-content-container rounded-xl bg-white p-6 shadow-md"
          >
            <slot name="sustainabilityIndex" />
          </div>

          <div class="mt-8 w-full max-w-content-container">
            <EventSection />
          </div>
        </div>
      </div>
    </div>
    <PdfViewerModal :url="pdfUrl" @close="closeViewer" />
  </div>
</template>

<script setup lang="ts">
import CarouselCapexTracker from '~/components/carousel/CapexTracker.vue'
import PdfViewerModal from '~/components/critical-insights/PdfViewerModal.vue'
import CarouselArticle from '~/components/market-insights/CarouselArticle.vue'
import EventSection from '~/components/portals/EventSection.vue'
import ReportsAndModelsSection from '~/components/portals/ReportsAndModelsSection.vue'
import VideoSection from '~/components/portals/VideoSection.vue'
import SectionTitle from '~/components/price-assessments/SectionTitle.vue'
import Breadcrumb from '~/components/shared/navigation/Breadcrumb.vue'
import ServicesHeroPortal from '~/components/shared/services/HeroPortal.vue'
import { ALL_MATERIALS_TOTALS } from '~/constants/capex-tracker-data'
import type { TGeneral } from '~/src/types/common'
import type { TEvent } from '~/src/types/events'
import type { TMarketSlug } from '~/src/types/markets'
import type { TReportCard } from '~/src/types/reports'
import useAuthStore from '~/stores/auth'
import { useBreadcrumbStore } from '~/stores/breadcrumb'
import { useModalStore } from '~/stores/modal'

type TProps = {
  general?: {
    slug: TMarketSlug
    icon: string
    subTitle: string
    listItems: string[]
    images: {
      desktop: string
      mobile: string
    }
    title: string
    category: string
    categoriesArticles?: string[]
    reportSlug?: string
    categoryReportPricing: string
    menu: string
  }
  reports?: TReportCard[]
  videos?: TGeneral[]
  events?: TEvent[]
  articles?: any[]
  services?: {
    checkboxes: string[]
  }
  isLoading: boolean
  esgData?: {
    checkbox?: string
  }
}

const props = withDefaults(defineProps<TProps>(), {
  isLoading: true,
  general: () => null,
  esgData: () => ({
    sustainabilityIndexes: [],
    checkbox: '',
  }),
  events: () => [],
  videos: () => [],
  reports: () => [],
  articles: () => [],
  services: () => ({
    checkboxes: [],
  }),
})

const slots = useSlots()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const modalStore = useModalStore()
const { modalParams } = storeToRefs(modalStore)

const loadingPricesPortal = ref(true)

const hasGlobalWeightedAverage = computed(() => !!slots.globalWeightedAverage)
const hasSustainabilityIndex = computed(() => !!slots.sustainabilityIndex)
const pdfUrl = computed(() => modalParams.value?.pdfUrl)
const product = computed(() => props.general.reportSlug || props.general.slug)
const category = computed(
  () => props.general.categoryReportPricing ?? product.value,
)
const isAuthenticated = computed(() => authStore.authenticated)

const closeViewer = () => {
  modalStore.setModalParams({ pdfUrl: '' })
}

const isClient = computed(() => typeof window !== 'undefined')
</script>
